import React, { useEffect, useRef, useState } from 'react';

import { CacheLocation, FpjsProvider } from '@fingerprintjs/fingerprintjs-pro-react';
import * as Sentry from '@sentry/react';
import { Routes, Navigate, Route } from 'react-router-dom';

import { bemCssModules, getFingerPrintData, getTestId } from '@utils';
import { useLocation } from '@utils/hooks';

import { useStore, userActions, globalActions, loginActions } from '@modules';
import { ChatContext } from '@modules/support';
import { TBranchData } from '@modules/user/types';

import CookiesNotification from '@components/CookiesNotification';
import CookieContext from '@components/CookiesNotification/context';
import CountDownNotification from '@components/CountDownNotification';
import FingerPrintInit from '@containers/FingerPrintInit';
import Notifications from '@containers/Notifications';
import GlobalLoaderLayout from '@layouts/GlobalLoaderLayout';

import ErrorBoundary from './ErrorBoundary';
import Head from './Head';
import UKBanner from './UKBanner';
import LoginRoute from './routes/login/LoginRoute';
import CountDownNotificationContextProvider from './routes/private/CountDownNotificationContext';
import PrivateRoute from './routes/private/PrivateRoute';

import style from './App.scss';

const cn = bemCssModules(style, 'App');
const ti = getTestId('App');

const App: CustomFC = ({ className }) => {
  const [{ login, global }, dispatch] = useStore();
  const { calcId, redirect, isUk } = global;
  const { to, state } = redirect;
  const [isLoading, setIsLoading] = useState(true);
  const ref = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as any });
    if (to) {
      dispatch(globalActions.clearRedirect());
    }
  }, [location.pathname]);

  useEffect(() => {
    dispatch(globalActions.appStarted());
    dispatch(globalActions.getDataByIp());
  }, []);

  useEffect(() => {
    if (!calcId) {
      dispatch(globalActions.getCalcStart())
        .unwrap()
        .finally(() => {
          dispatch(loginActions.keepAlive())
            .unwrap()
            .catch(error => error)
            .finally(() => setIsLoading(false));
        });
    }
  }, [calcId]);

  useEffect(() => {
    dispatch(globalActions.checkUKUserByIp());

    getFingerPrintData().then((res: Record<string, string>) => {
      dispatch(globalActions.setFingerPrintData(res));
    });
    window.branch.init(window.__APP_CONFIG__.branchKey, (error, data: TBranchData) => {
      dispatch(userActions.initBranch({ error, data }));
    });
  }, []);

  const shouldRedirect = !!(to && CONST.nav[to] && location.pathname !== CONST.nav[to]);

  return (
    <ErrorBoundary>
      <FpjsProvider
        cacheLocation={CacheLocation.Memory}
        loadOptions={{ apiKey: CONST.fingerPrintPublicKey }}
      >
        <FingerPrintInit />
        <CountDownNotificationContextProvider>
          <ChatContext>
            {shouldRedirect && <Navigate to={CONST.nav[to]} state={state} replace />}
            <Head />
            <GlobalLoaderLayout isLoading={isLoading || login.loaders.logout}>
              <div ref={ref} className={cn(null, [className])}>
                {/* isUk && <UKBanner /> */}
                <Routes>
                  <Route path={`${CONST.nav.login}/*`} element={<LoginRoute />} />
                  <Route path={`${CONST.nav.root}*`} element={<PrivateRoute />} />
                  <Route path="*" element={<Navigate to={CONST.nav.login} replace />} />
                </Routes>

                <Notifications />
                <CookieContext>
                  <CookiesNotification />
                </CookieContext>
                <CountDownNotification />
              </div>
            </GlobalLoaderLayout>
          </ChatContext>
        </CountDownNotificationContextProvider>
      </FpjsProvider>
    </ErrorBoundary>
  );
};

export default Sentry.withProfiler(App);
